import React, { useMemo, useState } from "react";
import {
    Search,
    MapPin,
    Briefcase,
    Clock,
    DollarSign,
    X,
    User,
    Lock,
    Mail,
    Phone,
    Upload,
    Send,
    Eye,
    LogOut,
    FileText,
    Save,
    ChevronRight,
} from "lucide-react";
import "./CareersPage.css";

const Modal = ({ open, title, children, footer, onClose, size = "md" }) => {
    if (!open) return null;

    return (
        <div className="crModal" role="dialog" aria-modal="true" onMouseDown={onClose}>
            <div
                className={`crModal__panel ${size === "lg" ? "is-lg" : ""}`}
                onMouseDown={(e) => e.stopPropagation()}
            >
                <div className="crModal__header">
                    <div className="crModal__title">{title}</div>
                    <button className="crIconBtn" onClick={onClose} type="button" aria-label="Fermer">
                        <X size={16} />
                    </button>
                </div>
                <div className="crModal__body">{children}</div>
                {footer && <div className="crModal__footer">{footer}</div>}
            </div>
        </div>
    );
};

export default function CareersPage() {
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedJob, setSelectedJob] = useState(null);
    const [showJobModal, setShowJobModal] = useState(false);
    const [showAuthModal, setShowAuthModal] = useState(false);
    const [showApplyModal, setShowApplyModal] = useState(false);
    const [showCGUModal, setShowCGUModal] = useState(false);
    const [showProfileModal, setShowProfileModal] = useState(false);
    const [isLogin, setIsLogin] = useState(true);
    const [isAuthenticated, setIsAuthenticated] = useState(true);

    const [profile, setProfile] = useState({
        name: "Emma Sabri",
        email: "emma.sabri@gmail.com",
        phone: "+212 6 12 34 56 78",
        title: "Développeur Full Stack",
        location: "Casablanca, Maroc",
        bio: "Passionnée par le développement web et les technologies modernes.",
        cvFile: null,
        cvName: "CV_Emma_Sabri.pdf",
    });

    const [user, setUser] = useState({ name: "Emma Sabri", email: "emma.sabri@gmail.com" });
    const [cvFile, setCvFile] = useState(null);
    const [isDragging, setIsDragging] = useState(false);

    const jobs = [
        {
            id: 1,
            title: "Développeur Full Stack",
            company: "TechCorp Solutions",
            location: "Casablanca",
            type: "CDI",
            salary: "15 000 – 20 000 MAD",
            posted: "Il y a 2 jours",
            description: "Rejoignez notre équipe dynamique pour travailler sur des projets innovants avec les dernières technologies web.",
            requirements: ["React.js", "Node.js", "3+ ans d'expérience", "SQL"],
            benefits: ["Assurance santé", "Télétravail partiel", "Formation continue"],
        },
        {
            id: 2,
            title: "Chef de Projet Digital",
            company: "Digital Innovations",
            location: "Rabat",
            type: "CDI",
            salary: "18 000 – 25 000 MAD",
            posted: "Il y a 1 jour",
            description: "Pilotez des projets digitaux d'envergure et coordonnez des équipes multidisciplinaires en environnement agile.",
            requirements: ["Agile", "Scrum", "5+ ans d'expérience", "Communication"],
            benefits: ["Prime", "Voiture de fonction", "Flex time"],
        },
        {
            id: 3,
            title: "Data Analyst",
            company: "Analytics Pro",
            location: "Casablanca",
            type: "CDI",
            salary: "14 000 – 19 000 MAD",
            posted: "Il y a 5 jours",
            description: "Transformez les données brutes en insights stratégiques pour accompagner la prise de décision de l'entreprise.",
            requirements: ["Python", "SQL", "Power BI", "Statistiques"],
            benefits: ["Bonus annuel", "Formation Big Data", "Bureau moderne"],
        },
        {
            id: 4,
            title: "Designer UX/UI",
            company: "Creative Studio",
            location: "Marrakech",
            type: "CDD",
            salary: "12 000 – 16 000 MAD",
            posted: "Il y a 3 jours",
            description: "Créez des expériences utilisateur fluides et des interfaces modernes pour des produits digitaux à fort impact.",
            requirements: ["Figma", "Adobe XD", "2+ ans d'expérience"],
            benefits: ["Horaires flexibles", "Équipement Mac", "Projets variés"],
        },
    ];

    const filteredJobs = useMemo(() => {
        const q = searchTerm.toLowerCase().trim();
        if (!q) return jobs;
        return jobs.filter(
            (job) =>
                job.title.toLowerCase().includes(q) ||
                job.location.toLowerCase().includes(q) ||
                job.company.toLowerCase().includes(q)
        );
    }, [searchTerm]);

    const openJobDetails = (job) => { setSelectedJob(job); setShowJobModal(true); };
    const closeJobDetails = () => { setSelectedJob(null); setShowJobModal(false); };

    const handleApplyClick = (job) => {
        setSelectedJob(job);
        setShowJobModal(false);
        if (!isAuthenticated) { setShowAuthModal(true); } else { setShowApplyModal(true); }
    };

    const handleAuth = () => {
        setIsAuthenticated(true);
        setUser({ name: "Boussaa Jamila", email: "jamila.boussaa@blue-scratch.com" });
        setShowAuthModal(false);
        setShowApplyModal(true);
    };

    const handleLogout = () => { setIsAuthenticated(false); setUser(null); };
    const handleSubmitApplication = () => { alert("Candidature envoyée avec succès !"); setShowApplyModal(false); setSelectedJob(null); };

    const handleDragOver = (e) => { e.preventDefault(); setIsDragging(true); };
    const handleDragLeave = () => setIsDragging(false);

    const handleDrop = (e) => {
        e.preventDefault(); setIsDragging(false);
        const file = e.dataTransfer.files?.[0];
        if (file?.type === "application/pdf") setCvFile(file);
    };

    const handleFileChange = (e) => {
        const file = e.target.files?.[0];
        if (file?.type === "application/pdf") setCvFile(file);
    };

    const handleProfileFileChange = (e) => {
        const file = e.target.files?.[0];
        if (file?.type === "application/pdf") setProfile((p) => ({ ...p, cvFile: file, cvName: file.name }));
    };

    const handleProfileDrop = (e) => {
        e.preventDefault(); setIsDragging(false);
        const file = e.dataTransfer.files?.[0];
        if (file?.type === "application/pdf") setProfile((p) => ({ ...p, cvFile: file, cvName: file.name }));
    };

    const handleRemoveCv = () => setProfile((p) => ({ ...p, cvFile: null, cvName: "" }));

    const handleProfileSave = () => {
        setUser({ name: profile.name, email: profile.email });
        alert("Profil mis à jour avec succès !");
        setShowProfileModal(false);
    };

    return (
        <div className="crPage">

            {/* ── Hero ── */}
            <div className="crHero">
                <div className="crWrap">
                    <div className="crTopBar">
                        <div className="crHero__content">
                            <h1 className="title">Trouvez votre prochain défi professionnel</h1>
                            <p>Des opportunités exceptionnelles vous attendent</p>
                        </div>

                        <div className="crTopBar__actions">
                            {isAuthenticated ? (
                                <>
                                    <span className="crUserInfo">Ravi de vous revoir, {user?.name.split(" ")[0]}</span>
                                    <button type="button" className="crBtn crBtn--ghost" onClick={() => setShowProfileModal(true)}>
                                        <User size={15} /> Mon Profil
                                    </button>
                                    <button type="button" className="crBtn crBtn--danger" onClick={handleLogout}>
                                        <LogOut size={15} />
                                    </button>
                                </>
                            ) : (
                                <>
                                    <button type="button" className="crBtn crBtn--ghost" onClick={() => { setIsLogin(false); setShowAuthModal(true); }}>
                                        S'inscrire
                                    </button>
                                    <button type="button" className="crBtn crBtn--primary" onClick={() => { setIsLogin(true); setShowAuthModal(true); }}>
                                        Connexion
                                    </button>
                                </>
                            )}
                        </div>
                    </div>

                    {/* Search */}
                    <div className="crSearchCard">
                        <div className="crSearchBox">
                            <Search size={20} color="#8fa0bb" />
                            <input
                                type="text"
                                placeholder="Poste, mot-clé, ville…"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                            <button className="crBtn crBtn--primary">
                                Rechercher
                                <ChevronRight size={15} />
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* ── Jobs ── */}
            <div className="crWrap">
                <div className="crSectionHeader">
                    <div>
                        <h2>Offres disponibles</h2>
                        <p>{filteredJobs.length} poste{filteredJobs.length !== 1 ? "s" : ""} trouvé{filteredJobs.length !== 1 ? "s" : ""}</p>
                    </div>
                </div>

                <div className="crGrid">
                    {filteredJobs.length === 0 ? (
                        <div className="crEmptyState">Aucune offre ne correspond à votre recherche.</div>
                    ) : (
                        filteredJobs.map((job) => (
                            <div key={job.id} className="crJobCard" onClick={() => openJobDetails(job)}>
                                <div className="crJobCard__top">
                                    <div>
                                        <h3>{job.title}</h3>
                                        <div className="crMuted">{job.company}</div>
                                    </div>
                                    <span className="crBadge">{job.type}</span>
                                </div>

                                <div className="crMeta">
                                    <div className="crMetaItem"><MapPin size={14} /><span>{job.location}</span></div>
                                    <div className="crMetaItem"><DollarSign size={14} /><span>{job.salary}</span></div>
                                    <div className="crMetaItem"><Clock size={14} /><span>{job.posted}</span></div>
                                </div>

                                <p className="crDescription">{job.description}</p>

                                <div className="crCardActions" onClick={(e) => e.stopPropagation()}>
                                    <button type="button" className="crBtn crBtn--ghost" onClick={() => openJobDetails(job)}>
                                        <Eye size={14} /> Voir détails
                                    </button>
                                    <button type="button" className="crBtn crBtn--primary" onClick={() => handleApplyClick(job)}>
                                        Postuler
                                    </button>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>

            {/* ── Footer ── */}
            <footer className="crFooter">
                <div className="crWrap crFooter__inner">
                    <div className="crFooter__left">© 2026 Cicada Careers. Tous droits réservés.</div>
                    <div className="crFooter__right">
                        <button type="button" className="crBtn crBtn--ghost" onClick={() => setShowCGUModal(true)}>
                            <FileText size={14} /> CGU
                        </button>
                    </div>
                </div>
            </footer>

            {/* ── Modal: Job Details ── */}
            <Modal
                open={showJobModal}
                title={selectedJob?.title ?? "Détails de l'offre"}
                onClose={closeJobDetails}
                size="lg"
                footer={
                    <>
                        <button type="button" className="crBtn crBtn--ghost" onClick={closeJobDetails}>Fermer</button>
                        <button type="button" className="crBtn crBtn--primary" onClick={() => handleApplyClick(selectedJob)}>
                            Postuler <ChevronRight size={14} />
                        </button>
                    </>
                }
            >
                {selectedJob && (
                    <div className="crDetails">
                        <div className="crDetailsGrid">
                            <div className="crMetaItem"><Briefcase size={15} /> {selectedJob.type}</div>
                            <div className="crMetaItem"><MapPin size={15} /> {selectedJob.location}</div>
                            <div className="crMetaItem"><DollarSign size={15} /> {selectedJob.salary}</div>
                            <div className="crMetaItem"><Clock size={15} /> {selectedJob.posted}</div>
                        </div>

                        <div className="crBlock">
                            <h4>Description du poste</h4>
                            <p style={{ fontSize: "14.5px", lineHeight: 1.7, color: "#475569" }}>{selectedJob.description}</p>
                        </div>

                        <div className="crBlock">
                            <h4>Compétences requises</h4>
                            <div className="crTagList">
                                {selectedJob.requirements.map((item, i) => (
                                    <span className="crTag" key={i}>{item}</span>
                                ))}
                            </div>
                        </div>

                        <div className="crBlock">
                            <h4>Avantages</h4>
                            <ul className="crBenefits">
                                {selectedJob.benefits.map((item, i) => <li key={i}>{item}</li>)}
                            </ul>
                        </div>
                    </div>
                )}
            </Modal>

            {/* ── Modal: Auth ── */}
            <Modal
                open={showAuthModal}
                title={isLogin ? "Connexion" : "Créer un compte"}
                onClose={() => setShowAuthModal(false)}
                footer={
                    <>
                        <button type="button" className="crBtn crBtn--ghost" onClick={() => setShowAuthModal(false)}>Annuler</button>
                        <button type="button" className="crBtn crBtn--primary" onClick={handleAuth}>
                            {isLogin ? "Se connecter" : "S'inscrire"}
                        </button>
                    </>
                }
            >
                <div className="crForm">
                    {!isLogin && (
                        <div className="crField">
                            <label>Nom complet</label>
                            <div className="crInputWrap"><User size={15} /><input type="text" placeholder="Jean Dupont" /></div>
                        </div>
                    )}

                    <div className="crField">
                        <label>Email</label>
                        <div className="crInputWrap"><Mail size={15} /><input type="email" placeholder="email@example.com" /></div>
                    </div>

                    <div className="crField">
                        <label>Mot de passe</label>
                        <div className="crInputWrap"><Lock size={15} /><input type="password" placeholder="••••••••" /></div>
                    </div>

                    {!isLogin && (
                        <>
                            <div className="crField">
                                <label>Téléphone</label>
                                <div className="crInputWrap"><Phone size={15} /><input type="tel" placeholder="+212 6XX XXX XXX" /></div>
                            </div>

                            <div className="crField">
                                <label>CV (PDF)</label>
                                <div
                                    className={`crUploadZone ${isDragging ? "is-dragging" : ""}`}
                                    onDragOver={handleDragOver}
                                    onDragLeave={handleDragLeave}
                                    onDrop={handleDrop}
                                >
                                    <Upload size={22} />
                                    <div>{cvFile ? cvFile.name : "Glissez-déposez votre CV ici"}</div>
                                    <label className="crUploadBtn">
                                        Choisir un fichier
                                        <input type="file" accept=".pdf" hidden onChange={handleFileChange} />
                                    </label>
                                </div>
                            </div>
                        </>
                    )}

                    <div className="crAuthSwitch">
                        <button type="button" className="crLinkBtn" onClick={() => setIsLogin((p) => !p)}>
                            {isLogin ? "Pas encore de compte ? S'inscrire" : "Déjà inscrit ? Se connecter"}
                        </button>
                    </div>
                </div>
            </Modal>

            {/* ── Modal: Candidature ── */}
            <Modal
                open={showApplyModal}
                title={selectedJob ? `Postuler — ${selectedJob.title}` : "Postuler"}
                onClose={() => setShowApplyModal(false)}
                size="lg"
                footer={
                    <>
                        <button type="button" className="crBtn crBtn--ghost" onClick={() => setShowApplyModal(false)}>Annuler</button>
                        <button type="button" className="crBtn crBtn--primary" onClick={handleSubmitApplication}>
                            <Send size={14} /> Envoyer
                        </button>
                    </>
                }
            >
                <div className="crForm">
                    <div className="crInfoBox">
                        <strong>Candidat :</strong> {user?.name} — {user?.email}
                    </div>
                    <div className="crField">
                        <label>Lettre de motivation</label>
                        <textarea rows={6} placeholder="Expliquez pourquoi vous êtes le candidat idéal pour ce poste…" />
                    </div>
                </div>
            </Modal>

            {/* ── Modal: CGU ── */}
            <Modal
                open={showCGUModal}
                title="Conditions Générales d'Utilisation"
                onClose={() => setShowCGUModal(false)}
                size="lg"
                footer={
                    <button type="button" className="crBtn crBtn--primary" onClick={() => setShowCGUModal(false)}>
                        Fermer
                    </button>
                }
            >
                <div className="crCguText">
                    {[
                        ["ARTICLE 1 : Objet", "Les présentes conditions générales d'utilisation ont pour objet l'encadrement juridique des modalités de mise à disposition des services du site et leur utilisation par l'utilisateur."],
                        ["ARTICLE 2 : Mentions légales", "L'édition du site est assurée par la société éditrice et l'hébergement est assuré par le prestataire technique concerné."],
                        ["ARTICLE 3 : Définitions", "Utilisateur, membre, contenu utilisateur, identifiant et mot de passe sont les principaux termes utilisés dans le cadre de la plateforme."],
                        ["ARTICLE 4 : Accès aux services", "Le site est accessible gratuitement à tout utilisateur disposant d'un accès Internet. Certains services peuvent être réservés aux membres connectés."],
                        ["ARTICLE 5 : Propriété intellectuelle", "Les marques, contenus, logos et éléments graphiques du site sont protégés conformément aux textes applicables."],
                        ["ARTICLE 6 : Données personnelles", "Les données collectées sont traitées dans le cadre de l'administration, de la gestion du service et de la relation avec l'utilisateur."],
                        ["ARTICLE 7 : Responsabilité", "L'utilisateur reste responsable de l'usage qu'il fait des services, informations et contenus mis à sa disposition."],
                        ["ARTICLE 8 : Liens hypertextes", "Les liens externes présents sur le site ne sauraient engager la responsabilité de l'éditeur concernant les contenus tiers."],
                        ["ARTICLE 9 : Évolution du contrat", "Le site se réserve le droit de modifier les présentes conditions générales à tout moment."],
                        ["ARTICLE 10 : Droit applicable", "La législation applicable est celle prévue dans le cadre juridique mentionné par l'éditeur du site."],
                    ].map(([title, text]) => (
                        <div key={title}>
                            <h4>{title}</h4>
                            <p>{text}</p>
                        </div>
                    ))}
                </div>
            </Modal>

            {/* ── Modal: Profil ── */}
            <Modal
                open={showProfileModal}
                title="Mon Profil"
                onClose={() => setShowProfileModal(false)}
                size="lg"
                footer={
                    <>
                        <button type="button" className="crBtn crBtn--ghost" onClick={() => setShowProfileModal(false)}>Annuler</button>
                        <button type="button" className="crBtn crBtn--primary" onClick={handleProfileSave}>
                            <Save size={14} /> Enregistrer
                        </button>
                    </>
                }
            >
                <div className="crProfileModal">
                    <div className="crProfileTop">
                        <div className="crAvatar">
                            {profile.name.split(" ").map((n) => n[0]).join("").toUpperCase()}
                        </div>
                        <div>
                            <div style={{ fontFamily: "'Syne', sans-serif", fontSize: "18px", fontWeight: 700, color: "#0f1f3d", marginBottom: "3px" }}>
                                {profile.name}
                            </div>
                            <div className="crMuted">{profile.title}</div>
                        </div>
                    </div>

                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px", marginTop: "22px" }}>
                        <div className="crField">
                            <label>Nom complet</label>
                            <div className="crInputWrap">
                                <User size={14} />
                                <input type="text" value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} />
                            </div>
                        </div>
                        <div className="crField">
                            <label>Email</label>
                            <div className="crInputWrap">
                                <Mail size={14} />
                                <input type="email" value={profile.email} onChange={(e) => setProfile({ ...profile, email: e.target.value })} />
                            </div>
                        </div>
                        <div className="crField">
                            <label>Téléphone</label>
                            <div className="crInputWrap">
                                <Phone size={14} />
                                <input type="tel" value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} />
                            </div>
                        </div>
                        <div className="crField">
                            <label>Ville, Pays</label>
                            <div className="crInputWrap">
                                <MapPin size={14} />
                                <input type="text" value={profile.location} onChange={(e) => setProfile({ ...profile, location: e.target.value })} />
                            </div>
                        </div>
                    </div>

                    <div className="crField" style={{ marginTop: "14px" }}>
                        <label>Bio / Présentation</label>
                        <textarea
                            rows={4}
                            value={profile.bio}
                            onChange={(e) => setProfile({ ...profile, bio: e.target.value })}
                            style={{
                                width: "100%",
                                border: "1.5px solid #e4eaf3",
                                borderRadius: "10px",
                                padding: "12px 16px",
                                fontFamily: "'DM Sans', sans-serif",
                                fontSize: "14px",
                                color: "#0f1f3d",
                                background: "#f7f9fc",
                                resize: "vertical",
                                outline: "none",
                                lineHeight: 1.65,
                            }}
                            onFocus={(e) => { e.target.style.borderColor = "#2d5be3"; e.target.style.background = "#fff"; e.target.style.boxShadow = "0 0 0 3px rgba(45,91,227,0.08)"; }}
                            onBlur={(e) => { e.target.style.borderColor = "#e4eaf3"; e.target.style.background = "#f7f9fc"; e.target.style.boxShadow = "none"; }}
                        />
                    </div>

                    <div className="crField" style={{ marginTop: "14px" }}>
                        <label>Mon CV (PDF)</label>
                        <div
                            className={`crUploadZone ${isDragging ? "is-dragging" : ""}`}
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            onDrop={handleProfileDrop}
                        >
                            <Upload size={20} />
                            <div>{profile.cvName || "Aucun CV importé"}</div>
                            <label className="crUploadBtn">
                                Choisir un fichier
                                <input type="file" accept=".pdf" hidden onChange={handleProfileFileChange} />
                            </label>
                        </div>
                        {profile.cvName && (
                            <div className="crUploadedFile">
                                <span>{profile.cvName}</span>
                                <button type="button" className="crIconBtn" onClick={handleRemoveCv}><X size={13} /></button>
                            </div>
                        )}
                    </div>
                </div>
            </Modal>
        </div>
    );
}